import type { CSSProperties, ReactElement } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ImageConfigResponse, StockPhoto } from '@shared/api';
import { getImageConfig, saveProposalMedia, searchStockImages } from '../api/client';
import { pickerLayout, positionPicker } from './imagePickerGeometry';

let imageConfigPromise: Promise<ImageConfigResponse> | null = null;
const loadImageConfig = (): Promise<ImageConfigResponse> =>
  (imageConfigPromise ??= getImageConfig().catch((): ImageConfigResponse => ({ provider: 'none' })));

const searchCache = new Map<string, StockPhoto[]>();

/** Keeps the picker deliberately constrained to four useful, distinct options. */
export function pickFourPhotos(photos: StockPhoto[]): StockPhoto[] {
  const seen = new Set<string>();
  return photos.filter((photo) => {
    if (!photo.id || !photo.src || seen.has(photo.id)) return false;
    seen.add(photo.id);
    return true;
  }).slice(0, 4);
}

type PickerState = 'closed' | 'loading' | 'ready' | 'replacing' | 'error';
type PickerPosition = { left: number; top: number; width: number; aspectRatio: number };

const isImageTarget = (element: Element): element is HTMLElement =>
  element instanceof HTMLElement
  && (element instanceof HTMLImageElement
    || Boolean(element.querySelector('img'))
    || /image|photo|visual|media/.test(element.className));

const precedingImageTarget = (root: HTMLElement): HTMLElement | null => {
  let candidate = root.previousElementSibling;
  while (candidate) {
    if (isImageTarget(candidate)) return candidate;
    candidate = candidate.previousElementSibling;
  }
  return null;
};

/**
 * A direct, contextual replacement flow. `defaultQuery` is authored by the
 * slide that owns the slot (headline, tactic, audience, or industry), so the
 * picker never exposes a search field or a media-library workflow.
 */
export function ImageSlot({
  url,
  alt,
  readOnly,
  onSave,
  className,
  hideImage,
  proposalId,
  defaultQuery,
}: {
  url: string | undefined;
  alt: string;
  readOnly: boolean;
  onSave: (url: string) => void;
  className?: string;
  hideImage?: boolean;
  proposalId: string;
  defaultQuery: string;
  /** Retained for existing callers; replacement always uses the contextual flow. */
  emptyState?: 'template' | 'neutral';
}): ReactElement | null {
  const [state, setState] = useState<PickerState>('closed');
  const [photos, setPhotos] = useState<StockPhoto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [position, setPosition] = useState<PickerPosition | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const cancelledRef = useRef(false);
  const requestFrameRef = useRef<number | null>(null);

  const closePicker = () => {
    cancelledRef.current = true;
    if (requestFrameRef.current !== null) cancelAnimationFrame(requestFrameRef.current);
    requestFrameRef.current = null;
    setState('closed');
    setError(null);
  };

  const setPickerPosition = () => {
    const root = rootRef.current;
    if (!root) return;
    const previousImage = precedingImageTarget(root);
    const targetElement = hideImage && previousImage
      ? previousImage
      : hideImage
        ? root.parentElement
        : root;
    const boundaryElement = root.closest<HTMLElement>('.page-frame');
    const slot = targetElement?.getBoundingClientRect();
    const trigger = triggerRef.current?.getBoundingClientRect();
    if (!slot || !trigger) return;
    const boundary = boundaryElement?.getBoundingClientRect() ?? {
      left: 0,
      top: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
      width: window.innerWidth,
      height: window.innerHeight,
    };
    const aspectRatio = slot.width > 0 && slot.height > 0 ? slot.width / slot.height : 4 / 3;
    const layout = pickerLayout(
      aspectRatio,
      Math.max(0, boundary.width - 24),
      Math.max(0, boundary.height - 24),
    );
    const rendered = pickerRef.current?.getBoundingClientRect();
    const placed = positionPicker(trigger, {
      width: rendered?.width ?? layout.width,
      height: rendered?.height ?? layout.estimatedHeight,
    }, boundary, slot);
    setPosition((current) =>
      current
      && current.left === placed.left
      && current.top === placed.top
      && current.width === layout.width
      && current.aspectRatio === aspectRatio
        ? current
        : { ...placed, width: layout.width, aspectRatio },
    );
  };

  const findPhotos = () => {
    const query = defaultQuery.trim();
    if (!query) {
      setError("Couldn't find images for this slide.");
      setState('error');
      return;
    }
    cancelledRef.current = false;
    setState('loading');
    setError(null);
    const cached = searchCache.get(query.toLowerCase());
    if (cached) {
      setPhotos(cached);
      setState('ready');
      return;
    }
    void loadImageConfig()
      .then((config) => {
        if (config.provider === 'none') throw new Error("Couldn't find images for this slide.");
        return searchStockImages(query);
      })
      .then((result) => {
        if (cancelledRef.current) return;
        if ('error' in result) throw new Error(result.error);
        const next = pickFourPhotos(result.photos);
        if (next.length !== 4) throw new Error("Couldn't find images for this slide.");
        searchCache.set(query.toLowerCase(), next);
        setPhotos(next);
        setState('ready');
      })
      .catch(() => {
        if (cancelledRef.current) return;
        setError("Couldn't find images for this slide.");
        setState('error');
      });
  };

  const openPicker = () => {
    cancelledRef.current = false;
    setPickerPosition();
    setPhotos([]);
    setState('loading');
    // Defer just enough for the browser to paint the four skeletons first.
    requestFrameRef.current = requestAnimationFrame(() => {
      requestFrameRef.current = null;
      findPhotos();
    });
  };

  const pickPhoto = (photo: StockPhoto) => {
    setState('replacing');
    void saveProposalMedia(proposalId, photo.src)
      .then((result) => {
        if ('error' in result) throw new Error(result.error);
        onSave(result.mediaUrl);
        closePicker();
      })
      .catch(() => {
        setError("Couldn't find images for this slide.");
        setState('error');
      });
  };

  useEffect(() => {
    if (readOnly) return undefined;
    void loadImageConfig();
    return undefined;
  }, [readOnly]);

  // Measure the actual card grid before paint. This covers portrait cards
  // whose height is much larger than a generic popover estimate.
  useLayoutEffect(() => {
    if (state !== 'closed') setPickerPosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, photos.length]);

  useEffect(() => {
    if (state === 'closed') return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      closePicker();
    };
    const onResize = () => setPickerPosition();
    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('resize', onResize);
    };
    // closePicker and setPickerPosition intentionally read current refs/state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  if (readOnly && !url) return null;
  const pickerOpen = state !== 'closed' && position;
  const pickerStyle = position
    ? ({ '--picker-left': `${position.left}px`, '--picker-top': `${position.top}px`, '--picker-width': `${position.width}px`, '--slot-ratio': String(position.aspectRatio) } as CSSProperties)
    : undefined;

  return (
    <div ref={rootRef} className={`image-slot${url ? '' : ' image-slot-empty'}${state !== 'closed' ? ' image-slot-active' : ''}${className ? ` ${className}` : ''}`}>
      {url && !hideImage ? <img src={url} alt={alt} /> : null}
      {!readOnly ? (
        <button ref={triggerRef} type="button" className="image-slot-edit" onClick={openPicker} aria-expanded={state !== 'closed'}>
          {url ? 'Replace image' : 'Add image'}
        </button>
      ) : null}
      {pickerOpen && typeof document !== 'undefined'
        ? createPortal(
          <div className="image-picker-layer" onMouseDown={closePicker} onWheel={closePicker}>
            <div ref={pickerRef} className="image-picker" role="dialog" aria-label="Choose a replacement image" style={pickerStyle} onMouseDown={(event) => event.stopPropagation()}>
              {state === 'error' ? (
                <div className="image-picker-failure">
                  <span>{error || "Couldn't find images for this slide."}</span>
                  <button type="button" className="image-picker-retry" onClick={findPhotos}>Try again</button>
                </div>
              ) : (
                <div className="image-picker-grid" aria-label={state === 'loading' ? 'Finding images' : undefined}>
                  {state === 'loading'
                    ? Array.from({ length: 4 }, (_, index) => <span key={index} className="image-picker-skeleton" aria-hidden="true" />)
                    : photos.map((photo) => (
                      <button key={photo.id} type="button" className="image-picker-thumb" disabled={state === 'replacing'} onClick={() => pickPhoto(photo)}>
                        <img src={photo.thumb} alt={photo.alt || 'Replacement image'} />
                      </button>
                    ))}
                </div>
              )}
            </div>
          </div>,
          document.body,
        )
        : null}
    </div>
  );
}
